import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../adapters/api';
import { CartContext } from './CartContext';
import { AuthContext } from './AuthContext';

export const NotificationContext = createContext();

export const PRIORITY_CONFIG = {
  urgent:  { emoji: '🔴', label: 'ด่วนมาก', color: '#b71c1c', bg: '#ffebee' },
  high:    { emoji: '🟠', label: 'ด่วน',    color: '#e65100', bg: '#fff3e0' },
  today:   { emoji: '🟡', label: 'วันนี้',   color: '#f57f17', bg: '#fffde7' },
  waiting: { emoji: '🔵', label: 'รอ',       color: '#1565c0', bg: '#e3f2fd' },
};

// Where clicking a notification (bell item or desktop toast) should take the user
export function notificationPath(type, cartGuid, refDocNo) {
  if (type === 'DRAFT_DELETED') return cartGuid ? `/orderlist?tab=DRAFT&view_deleted=${cartGuid}` : null;
  if (type === 'QT_CREATED') {
    return refDocNo ? `/orderlist?tab=QT&highlight=${encodeURIComponent(refDocNo)}` : '/orderlist?tab=QT';
  }
  const qs = cartGuid ? `&highlight=${cartGuid}` : '';
  return `/orderlist?tab=DRAFT${qs}`;
}

// One toast per document+event — shared by the OS (dedupe across tabs) and bell ↔ toast read sync
function toastTag(type, cartGuid, refDocNo) {
  return `ccs-${cartGuid || refDocNo}-${type}`;
}

// Same rule as backend isSalesActor (backend/utils/channelMap.js)
const SALES_GROUPS = ['แผนกขายเครดิต', 'แผนกขายออนไลน์', 'แผนกขายหน้าร้าน'];
const WALKIN_BRANCH_GROUPS = ['ขายหน้าร้านสันทราย', 'ขายหน้าร้านสันกำแพง'];
function isSalesUser(dept = []) {
  return dept.includes('ฝ่ายขาย') || dept.some(d =>
    SALES_GROUPS.some(g => d.includes(g)) || WALKIN_BRANCH_GROUPS.includes(String(d).trim()));
}

const desktopSupported = typeof window !== 'undefined' && 'Notification' in window;

// Colours match the keyword colours in NotificationBell
function toastStyle(message) {
  switch (message.notif_type) {
    case 'DRAFT_DELETED':  return { kind: 'DRAFT', label: 'ลบ',     color: '#c62828' };
    case 'DRAFT_RESTORED': return { kind: 'DRAFT', label: 'กู้คืน',  color: '#00695c' };
    case 'QT_CREATED':     return { kind: 'QT',    label: 'สร้าง',   color: '#1976d2' };
    default:
      return message.message?.includes('แก้ไข Draft')
        ? { kind: 'DRAFT', label: 'แก้ไข', color: '#e65100' }
        : { kind: 'DRAFT', label: 'ใหม่',  color: '#7b1fa2' };
  }
}

// The toast's left-hand picture is the only visual we control — draw a coloured card per action
// (+ a priority band for urgent/high) so the event type is recognisable at a glance.
const toastIconCache = new Map();
function buildToastIcon({ kind, label, color }, priority) {
  const key = `${kind}|${label}|${color}|${priority || ''}`;
  if (toastIconCache.has(key)) return toastIconCache.get(key);

  const S = 192;
  const canvas = document.createElement('canvas');
  canvas.width = S;
  canvas.height = S;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '/logo192.png';
  const FONT = '"Segoe UI", "Leelawadee UI", Tahoma, sans-serif';

  ctx.fillStyle = color;
  ctx.beginPath();
  if (ctx.roundRect) ctx.roundRect(0, 0, S, S, 36);
  else ctx.rect(0, 0, S, S);
  ctx.fill();
  ctx.clip(); // keep the priority band inside the rounded corners

  const p = PRIORITY_CONFIG[priority];
  const band = p ? 50 : 0;

  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(255,255,255,0.75)';
  ctx.font = `bold 26px ${FONT}`;
  ctx.fillText(kind, S / 2, 44);

  ctx.fillStyle = '#fff';
  ctx.font = `bold 58px ${FONT}`;
  ctx.textBaseline = 'middle';
  ctx.fillText(label, S / 2, (S - band) / 2 + 22, S - 24);

  if (p) {
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, S - band, S, band);
    ctx.fillStyle = p.color;
    ctx.font = `bold 30px ${FONT}`;
    ctx.fillText(`● ${p.label}`, S / 2, S - band / 2 + 2);
  }

  const url = canvas.toDataURL('image/png');
  toastIconCache.set(key, url);
  return url;
}

export const NotificationProvider = ({ children }) => {
  const { addNotificationListener } = useContext(CartContext);
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  // navigate changes identity on every route change — keep it in a ref so the WS effect doesn't re-run
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [desktopPermission, setDesktopPermission] = useState(
    desktopSupported ? Notification.permission : 'unsupported'
  );
  const originalFaviconHref = useRef(null);
  const notificationsRef = useRef(notifications);
  notificationsRef.current = notifications;
  // Toasts shown by this tab, keyed by tag — so reading in the bell can dismiss them
  const openToastsRef = useRef(new Map());

  const closeToast = useCallback((tag) => {
    const n = openToastsRef.current.get(tag);
    if (n) n.close();
    openToastsRef.current.delete(tag);
  }, []);

  const markRead = useCallback(async (id) => {
    try {
      await api.patch(`/api/notifications/${id}/read`);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      const notif = notificationsRef.current.find(n => n.id === id);
      if (notif) closeToast(toastTag(notif.type, notif.cart_guid, notif.ref_doc_no));
    } catch (err) {
      console.error('markRead error:', err);
    }
  }, [closeToast]);

  const markAllRead = useCallback(async () => {
    try {
      await api.post('/api/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      for (const tag of [...openToastsRef.current.keys()]) closeToast(tag);
    } catch (err) {
      console.error('markAllRead error:', err);
    }
  }, [closeToast]);

  const showDesktopNotification = useCallback((message) => {
    if (!desktopSupported || Notification.permission !== 'granted') return;
    // Scannable layout — icon: action + priority, title: who, body: what they did
    const actor = message.actor_name || '';
    const action = actor && message.message?.startsWith(`${actor} `)
      ? message.message.slice(actor.length + 1)
      : message.message;
    const priority = message.notif_type !== 'DRAFT_DELETED' && ['urgent', 'high'].includes(message.priority)
      ? message.priority : null;
    const title = actor || 'CCS ERP';
    const body = action;
    try {
      let icon = '/logo192.png';
      try { icon = buildToastIcon(toastStyle(message), priority); } catch (e) { /* keep logo */ }
      // Same tag across tabs → the OS replaces instead of stacking duplicates
      const tag = toastTag(message.notif_type, message.cart_guid, message.ref_doc_no);
      const n = new Notification(title, {
        body,
        icon,
        tag,
        requireInteraction: ['urgent', 'high'].includes(message.priority),
      });
      openToastsRef.current.set(tag, n);
      n.onclose = () => {
        if (openToastsRef.current.get(tag) === n) openToastsRef.current.delete(tag);
      };
      n.onclick = () => {
        window.focus();
        closeToast(tag);
        // Clicking the toast counts as reading it — mark the matching bell item(s) read
        notificationsRef.current
          .filter(x => !x.is_read && toastTag(x.type, x.cart_guid, x.ref_doc_no) === tag)
          .forEach(x => markRead(x.id));
        const path = notificationPath(message.notif_type, message.cart_guid, message.ref_doc_no);
        if (path) navigateRef.current(path);
      };
    } catch (err) {
      console.error('showDesktopNotification error:', err);
    }
  }, [closeToast, markRead]);

  // Preview toast built from the user's latest real notification (falls back to a sample draft)
  const showTestNotification = useCallback(() => {
    const latest = notifications[0];
    showDesktopNotification(latest ? {
      message: latest.message,
      actor_name: latest.creator_name,
      notif_type: latest.type,
      priority: latest.draft_priority,
      cart_guid: latest.cart_guid,
      ref_doc_no: latest.ref_doc_no,
    } : {
      message: `${currentUser?.user_name || 'เซลล์'} บันทึก Draft (DRAFT-ทดสอบ)`,
      actor_name: currentUser?.user_name || 'เซลล์',
      notif_type: 'DRAFT_SAVED',
      priority: 'high',
      cart_guid: null,
      ref_doc_no: 'test-sample',
    });
  }, [notifications, currentUser, showDesktopNotification]);

  const requestDesktopPermission = useCallback(async () => {
    if (!desktopSupported) return;
    try {
      const result = await Notification.requestPermission();
      setDesktopPermission(result);
      // Show one right away so the user sees what they just turned on
      if (result === 'granted') showTestNotification();
    } catch (err) {
      console.error('requestDesktopPermission error:', err);
    }
  }, [showTestNotification]);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await api.get('/api/notifications');
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (err) {
      console.error('fetchNotifications error:', err);
    }
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    fetchNotifications();
    const myCode = currentUser.user_code?.toUpperCase();
    const iAmSales = isSalesUser(currentUser.dept);
    const unsubscribe = addNotificationListener((message) => {
      if (message.type === 'DRAFT_NOTIFICATION') {
        // recipients field is now always present; fall back to fetch-always if absent
        if (!message.recipients || message.recipients.some(r => r.toUpperCase() === myCode)) {
          fetchNotifications();
          // Desktop toast only for cross-department handoffs (sales ↔ ธุรการ)
          if (typeof message.from_sales === 'boolean' && message.from_sales !== iAmSales) {
            showDesktopNotification(message);
          }
        }
      } else if (message.type === 'DRAFT_LIST_UPDATED') {
        // Draft deletions clean up DB notifications — refresh so the bell stays accurate
        fetchNotifications();
      }
    });
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      fetchNotifications();
      // Permission may have been changed from the browser's site settings
      if (desktopSupported) setDesktopPermission(Notification.permission);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [currentUser, fetchNotifications, addNotificationListener, showDesktopNotification]);

  useEffect(() => {
    const faviconEl = document.querySelector("link[rel~='icon']");
    if (!faviconEl) return;

    if (!originalFaviconHref.current) {
      originalFaviconHref.current = faviconEl.href;
    }

    if (unreadCount > 0) {
      let cancelled = false;
      const canvas = document.createElement('canvas');
      canvas.width = 32;
      canvas.height = 32;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        if (cancelled) return;
        ctx.drawImage(img, 0, 0, 32, 32);
        ctx.beginPath();
        ctx.arc(26, 6, 7, 0, 2 * Math.PI);
        ctx.fillStyle = '#e53935';
        ctx.fill();
        faviconEl.href = canvas.toDataURL('image/png');
      };
      img.onerror = () => {
        if (cancelled) return;
        ctx.beginPath();
        ctx.arc(26, 6, 7, 0, 2 * Math.PI);
        ctx.fillStyle = '#e53935';
        ctx.fill();
        faviconEl.href = canvas.toDataURL('image/png');
      };
      img.src = originalFaviconHref.current || '/favicon7.ico';
      return () => { cancelled = true; };
    } else {
      faviconEl.href = originalFaviconHref.current || '/favicon7.ico';
    }
  }, [unreadCount]);

  useEffect(() => {
    const baseTitle = 'CCS ERP';

    if (unreadCount > 0) {
      document.title = `(${unreadCount}) ${baseTitle}`;
    } else {
      document.title = baseTitle;
    }

    return () => {
      document.title = baseTitle;
    };
  }, [unreadCount]);

  return (
    <NotificationContext.Provider value={{
      notifications, unreadCount, markRead, markAllRead, fetchNotifications,
      desktopPermission, requestDesktopPermission, showTestNotification,
    }}>
      {children}
    </NotificationContext.Provider>
  );
};
